import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { services } from "../data/services";
import { markets, REGION_META } from "../data/markets";
import { insights } from "../data/insights";
import { InsightCard } from "../components/InsightCard";
import RegionMap from "../components/RegionMap";
import { Reveal } from "../components/Reveal";
import { useTitle } from "../lib/useTitle";

const VALUES = [
  { name: "One team", desc: "One point of contact across every country and service.", color: "var(--blue)" },
  { name: "Local depth", desc: "People who know each market's registries and tax offices.", color: "var(--emerald)" },
  { name: "Clear advice", desc: "A recommendation, not a list of options.", color: "var(--saffron-ink)" },
  { name: "Done properly", desc: "Documented so banks, auditors and regulators can follow it.", color: "var(--coral)" },
];

const FIGURES = [
  { value: "1,000+", label: "companies set up" },
  { value: "60+", label: "countries served" },
  { value: "100+", label: "clients supported" },
];

const STEPS = [
  { n: "1", title: "Understand.", desc: "We map what you want to do, where, and the obligations that come with it." },
  { n: "2", title: "Set up.", desc: "Entities, registrations, bank accounts, payroll and books, in the right order." },
  { n: "3", title: "Run.", desc: "Monthly accounting, filings and reporting, with a CFO view when you need one." },
];

const scrollToAbout = (e) => {
  e.preventDefault();
  const el = document.getElementById("about");
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -76, duration: 1.1 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const highlightCountry = (iso) =>
  window.dispatchEvent(new CustomEvent("am:highlight-country", { detail: iso }));

const RegionCountries = ({ regionKey }) => {
  const meta = REGION_META[regionKey];
  return (
    <div>
      <p className="flex items-center gap-2 text-sm font-semibold text-ink">
        <span className="inline-block h-2 w-2 rounded-full" style={{ background: meta.color }} />
        {meta.label}
      </p>
      <div className="mt-2.5 flex flex-wrap gap-1.5">
        {markets
          .filter((m) => m.region === regionKey)
          .map((m) => (
            <Link
              key={m.iso}
              to={`/where-we-work?market=${encodeURIComponent(m.name)}`}
              data-testid={`about-pill-${m.iso}`}
              onMouseEnter={() => highlightCountry(m.iso)}
              onMouseLeave={() => highlightCountry(null)}
              onFocus={() => highlightCountry(m.iso)}
              onBlur={() => highlightCountry(null)}
              className="rounded-full border border-line bg-base px-3 py-1 text-xs text-mute transition-colors duration-200 hover:border-ink hover:text-ink"
            >
              {m.name}
            </Link>
          ))}
      </div>
    </div>
  );
};

export default function Home() {
  useTitle("Home | Amplified Minds");
  const corporate = services.find((s) => s.slug === "company-formation");
  const family = services.find((s) => s.slug === "private-wealth-family-office");

  return (
    <>
      <section className="border-b border-line bg-surface" data-testid="hero-section">
        <div className="mx-auto grid w-full max-w-[1200px] items-center gap-12 px-4 py-16 sm:px-6 md:py-24 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <p className="am-eyebrow" style={{ color: "var(--saffron-ink)" }}>
              India · Middle East · Africa
            </p>
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.05] text-ink sm:text-5xl lg:text-6xl">
              We are <span className="text-coral">Amplified Minds.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-mute">
              We set up, run and grow businesses across India, the Middle East and Africa: company formation,
              accounting, tax, HR and CFO support, plus private wealth and family office services for the families
              behind them.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/contact" className="am-btn px-7 py-3.5 text-sm" data-testid="hero-cta-talk">
                Talk to us
              </Link>
              <a href="#about" onClick={scrollToAbout} className="am-btn-ghost px-7 py-3.5 text-sm" data-testid="hero-cta-about">
                About us
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-[22px] border border-line bg-surface p-4 shadow-surface sm:p-6" data-testid="hero-map-panel">
              <RegionMap idPrefix="home" listenHighlight />
            </div>
          </Reveal>
        </div>

        <div className="mx-auto w-full max-w-[1200px] px-4 pb-16 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {services.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.07}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`quick-link-${s.slug}`}
                  className="flex h-full items-center gap-3 rounded-[14px] border border-line bg-base p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-ink"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg" style={{ background: s.soft }}>
                    <s.icon className="h-5 w-5" style={{ color: s.color }} aria-hidden="true" />
                  </span>
                  <span className="text-sm font-semibold leading-snug text-ink">{s.name}</span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-line bg-surface" data-testid="values-strip">
        <div className="mx-auto grid w-full max-w-[1200px] grid-cols-2 gap-8 px-4 py-10 sm:px-6 lg:grid-cols-4 lg:gap-0 lg:divide-x lg:divide-line lg:px-8">
          {VALUES.map((v, i) => (
            <Reveal key={v.name} delay={i * 0.07} className={i > 0 ? "lg:pl-8" : ""}>
              <div className="lg:pr-8" data-testid={`value-${i}`}>
                <b className="font-display text-base font-bold" style={{ color: v.color }}>
                  {v.name}
                </b>
                <p className="mt-1.5 text-sm leading-relaxed text-mute">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="what-we-do">
        <div className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <p className="am-eyebrow" style={{ color: "var(--blue)" }}>
                What we do
              </p>
              <h2 className="mt-4 font-display text-3xl font-bold text-ink sm:text-4xl">
                Support for businesses, and for the families who own them.
              </h2>
            </Reveal>
            <Reveal delay={0.1} className="self-end text-base leading-relaxed text-mute">
              Most clients start with one need, such as a new company or a tax filing, and add the rest as they
              grow. Each line has its own specialists; you deal with one team.
            </Reveal>
          </div>

          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            {[
              {
                service: corporate,
                eyebrow: "For businesses",
                title: "Corporate services",
                desc: "From the first registration to monthly books, payroll and board reporting, in every country you operate.",
                link: "Explore corporate services →",
                pills: ["Formation", "Accounting & Tax", "HR & Payroll", "CFO"],
                color: "var(--blue)",
              },
              {
                service: family,
                eyebrow: "For families",
                title: "Private wealth & family office",
                desc: "Structures, governance and reporting that protect what you have built and carry it to the next generation.",
                link: "Explore private wealth →",
                pills: ["Family office", "Succession", "Governance", "Reporting"],
                color: "var(--coral)",
              },
            ].map((card, i) => (
              <Reveal key={card.title} delay={i * 0.08}>
                <Link
                  to={`/services/${card.service.slug}`}
                  data-testid={`what-card-${i}`}
                  className="group block h-full overflow-hidden rounded-[20px] border border-line bg-surface shadow-surface transition-all duration-300 hover:-translate-y-1 hover:border-ink"
                >
                  <div className="relative h-40 overflow-hidden" style={{ background: card.service.soft }}>
                    <div
                      aria-hidden="true"
                      className="absolute -right-12 -top-12 h-48 w-48 rounded-full"
                      style={{ border: "14px solid color-mix(in srgb, " + card.color + " 16%, transparent)" }}
                    />
                    <div className="absolute bottom-4 left-5 flex flex-wrap gap-1.5 pr-5">
                      {card.pills.map((p) => (
                        <span key={p} className="rounded-full border border-line bg-surface px-3 py-1 text-xs font-medium text-ink">
                          {p}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="p-7">
                    <p className="am-eyebrow" style={{ color: card.color }}>
                      {card.eyebrow}
                    </p>
                    <h3 className="mt-2.5 font-display text-2xl font-bold text-ink">{card.title}</h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-mute">{card.desc}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: card.color }}>
                      {card.link}
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-24">
        <div className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-[24px] p-8 sm:p-12" style={{ background: "var(--ink)" }} data-testid="market-entry-banner">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full opacity-35 blur-3xl"
                style={{ background: "conic-gradient(from 90deg, var(--emerald), var(--blue), var(--coral), var(--saffron), var(--emerald))" }}
              />
              <div className="relative max-w-2xl">
                <p className="am-eyebrow" style={{ color: "var(--saffron)" }}>
                  Expanding into a new market?
                </p>
                <h2 className="mt-4 font-display text-3xl font-bold sm:text-4xl" style={{ color: "var(--bg)" }}>
                  Enter India, the Gulf or Africa with one partner.
                </h2>
                <p className="mt-4 text-base leading-relaxed" style={{ color: "color-mix(in srgb, var(--bg) 72%, transparent)" }}>
                  We handle the company, the bank account, the first hires and the first tax filings, so you can
                  focus on customers.
                </p>
                <Link to="/where-we-work" className="am-btn mt-7 px-7 py-3.5 text-sm" data-testid="banner-cta">
                  See where we work
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-16 md:pb-24" data-testid="team-experience">
        <div className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <p className="am-eyebrow" style={{ color: "var(--coral)" }}>
                Our team's experience
              </p>
              <p className="text-xs text-mute">Combined experience of the Amplified Minds team</p>
            </div>
            <div className="mt-4 h-0.5 bg-ink" aria-hidden="true" />
          </Reveal>
          <div className="mt-8 grid gap-8 sm:grid-cols-3">
            {FIGURES.map((f, i) => (
              <Reveal key={f.label} delay={i * 0.07}>
                <div data-testid={`figure-${i}`}>
                  <p className="font-display text-[3.6rem] font-extrabold leading-none tabular-nums text-ink">
                    {f.value}
                  </p>
                  <p className="mt-2 text-sm text-mute">{f.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="about" className="scroll-mt-24 pb-16 md:pb-24" data-testid="about-section">
        <div className="mx-auto grid w-full max-w-[1200px] gap-6 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <div className="h-full rounded-[20px] border border-line bg-surface p-7 shadow-surface sm:p-9">
              <p className="am-eyebrow" style={{ color: "var(--emerald)" }}>
                How we work
              </p>
              <h2 className="mt-3 font-display text-2xl font-bold text-ink sm:text-3xl">
                From first conversation to running smoothly.
              </h2>
              <ol className="mt-7 space-y-6">
                {STEPS.map((s) => (
                  <li key={s.n} className="flex gap-4">
                    <span className="font-display text-xl font-extrabold" style={{ color: "var(--emerald)" }}>
                      {s.n}.
                    </span>
                    <p className="text-sm leading-relaxed text-mute">
                      <b className="font-semibold text-ink">{s.title}</b> {s.desc}
                    </p>
                  </li>
                ))}
              </ol>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="h-full rounded-[20px] border border-line bg-surface p-7 shadow-surface sm:p-9">
              <p className="am-eyebrow" style={{ color: "var(--saffron-ink)" }}>
                Where we work
              </p>
              <h2 className="mt-3 font-display text-2xl font-bold text-ink sm:text-3xl">
                The markets we work in, across three regions.
              </h2>
              <div className="mt-7 space-y-5">
                <RegionCountries regionKey="in" />
                <RegionCountries regionKey="me" />
                <RegionCountries regionKey="af" />
              </div>
              <Link to="/where-we-work" className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-blue" data-testid="about-open-map">
                Open the map of all markets
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-line bg-surface py-16 md:py-24" data-testid="insights-preview">
        <div className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div className="max-w-xl">
                <p className="am-eyebrow" style={{ color: "var(--blue)" }}>
                  Insights
                </p>
                <h2 className="mt-3 font-display text-3xl font-bold text-ink sm:text-4xl">
                  Guides and updates for doing business in our markets.
                </h2>
              </div>
              <Link to="/insights" className="am-btn-ghost px-6 py-3 text-sm" data-testid="insights-more">
                Discover more insights →
              </Link>
            </div>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {insights.slice(0, 3).map((item, i) => (
              <Reveal key={item.slug} delay={i * 0.07}>
                <InsightCard item={item} />
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-16 rounded-[24px] p-8 text-center sm:p-12" style={{ background: "var(--ink)" }} data-testid="home-cta">
              <h2 className="font-display text-3xl font-bold sm:text-4xl" style={{ color: "var(--bg)" }}>
                Talk to our experts.
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed" style={{ color: "color-mix(in srgb, var(--bg) 72%, transparent)" }}>
                Share your plans and the countries you have in mind. We reply with a first view on structure and
                next steps.
              </p>
              <Link to="/contact" className="am-btn mt-7 px-7 py-3.5 text-sm" data-testid="home-cta-button">
                Get in touch
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
